'use client'

import React, { useRef } from 'react'
import { m, useReducedMotion } from 'framer-motion'
import { ArrowLeft } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { Link } from '@/i18n/navigation'
import { routing } from '@/i18n/routing'
import SpaceBackdrop from '@/components/world/SpaceBackdrop'
import { useFocusableTextBlocks } from '@/hooks/useFocusableTextBlocks'

type Icon = React.ComponentType<{ className?: string; style?: React.CSSProperties }>

/** Page frame shared by the imprint and privacy pages: space backdrop and a hero with a back button. */
export function LegalPage({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle: string
  children: React.ReactNode
}) {
  const tLegal = useTranslations('legal')
  const locale = useLocale()
  const reduce = useReducedMotion()
  const bodyRef = useRef<HTMLDivElement>(null)
  // Headings, paragraphs and lists are tab stops so keyboard users can step through the legal text.
  useFocusableTextBlocks(bodyRef)

  return (
    // Layout already renders <main>; this is just the page body.
    <div ref={bodyRef} className="relative min-h-screen overflow-x-clip">
      <SpaceBackdrop />

      <div className="relative z-10 mx-auto max-w-4xl px-6 pt-6 pb-10 sm:px-10">
        <m.header
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, type: 'spring', bounce: 0.3 }}
          className="mb-12 text-center"
        >
          {/* button pinned left, title centred on the page; padding keeps a long title clear of it */}
          <div className="relative flex items-center justify-center px-14 sm:px-20">
            {/* centred with flex, not translate: the hover scale below writes its own transform */}
            <div className="absolute inset-y-0 left-0 flex items-center">
              {/* whileTap makes framer-motion add tabIndex=0; the link inside is the real tab stop */}
              <m.div
                tabIndex={-1}
                whileHover={reduce ? undefined : { scale: 1.08 }}
                whileTap={reduce ? undefined : { scale: 0.92 }}
              >
                <Link
                  href="/"
                  aria-label={tLegal('backToHome')}
                  title={tLegal('backToHome')}
                  className="group relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/25 bg-white/[0.05] backdrop-blur-xl transition-colors hover:bg-white/[0.10] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-300 sm:h-14 sm:w-14"
                  style={{
                    boxShadow:
                      'inset 0 1px 0 0 rgba(255,255,255,0.35), inset 0 0 0 1px rgba(255,255,255,0.08), inset 0 0 24px -14px #22d3ee, 0 10px 30px -14px rgba(34,211,238,0.45)',
                  }}
                >
                  <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/[0.14] to-transparent" />
                  <ArrowLeft className="relative h-5 w-5 text-[var(--flagship-cyan)] transition-transform group-hover:-translate-x-0.5 sm:h-6 sm:w-6" />
                </Link>
              </m.div>
            </div>
            <h1 className="mb-0! min-w-0 [font-family:var(--font-display)] text-4xl break-words hyphens-auto text-[var(--flagship-text)] sm:text-5xl md:text-6xl">
              {title}
            </h1>
          </div>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-[var(--flagship-muted)]">{subtitle}</p>
          {/* The German text is the legally binding version; translations say so. */}
          {locale !== routing.defaultLocale && (
            <p className="mx-auto mt-3 max-w-2xl text-sm text-[var(--flagship-muted)] italic">
              {tLegal('translationNotice')}
            </p>
          )}
        </m.header>

        <div className="space-y-6">{children}</div>
      </div>
    </div>
  )
}

/**
 * A top-level legal section as a liquid glass card (matches the homepage's about cards),
 * tinted with one of the brand accents.
 */
export function GlassSection({
  title,
  icon: Icon,
  accent = 'var(--flagship-cyan)',
  children,
}: {
  title: string
  icon?: Icon
  accent?: string
  children: React.ReactNode
}) {
  // No scroll-in animation and no backdrop-filter: on tall text cards both lag behind fast scrolling
  // (cards show half-faded, blur repaints every frame). A tinted fill reads the same over the backdrop.
  return (
    <section
      className="relative overflow-hidden rounded-3xl border border-white/25 bg-[rgba(24,18,52,0.55)] p-6 md:p-8"
      style={
        {
          '--accent': accent,
          boxShadow:
            'inset 0 1px 0 0 rgba(255,255,255,0.35), inset 0 0 0 1px rgba(255,255,255,0.08), inset 0 0 50px -28px var(--accent), 0 16px 40px -18px color-mix(in srgb, var(--accent) 30%, transparent)',
        } as React.CSSProperties
      }
    >
      {/* specular top sheen + accent glow from the corner */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-white/[0.08] to-transparent" />
      <div
        className="pointer-events-none absolute -inset-px rounded-[inherit] opacity-40"
        style={{
          background:
            'radial-gradient(120% 80% at 0% 0%, color-mix(in srgb, var(--accent) 18%, transparent), transparent 55%)',
        }}
      />

      <div className="relative z-10">
        <div className="mb-5 flex items-center gap-3">
          {Icon && (
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/25 bg-white/[0.06]"
              style={{
                boxShadow: `inset 0 1px 0 0 rgba(255,255,255,0.35), 0 0 18px -6px ${accent}`,
              }}
            >
              <Icon className="h-5 w-5" style={{ color: accent }} />
            </div>
          )}
          <h2 className="[font-family:var(--font-display)] text-2xl text-[var(--flagship-text)] md:text-3xl">
            {title}
          </h2>
        </div>
        {/* The global (unlayered) p margin beats utilities, so zero it and space with gap instead:
            Tailwind 4's space-y also works through margin-bottom and would be cancelled with it. */}
        <div className="flex flex-col gap-3 leading-relaxed text-[var(--flagship-text)]/80 [&_a]:text-[var(--flagship-cyan)] [&_a:hover]:underline [&_p]:mb-0! [&_strong]:text-[var(--flagship-text)]">
          {children}
        </div>
      </div>
    </section>
  )
}

/** Sub-heading inside a GlassSection. */
export function SubSection({
  title,
  level = 3,
  children,
}: {
  title: string
  level?: 3 | 4
  children: React.ReactNode
}) {
  const Heading = level === 3 ? 'h3' : 'h4'
  return (
    <div className={level === 3 ? 'pt-2' : 'pt-1'}>
      <Heading
        className={`mb-2 [font-family:var(--font-ui)] font-bold text-[var(--flagship-text)] ${level === 3 ? 'text-lg md:text-xl' : 'text-base md:text-lg'}`}
      >
        {title}
      </Heading>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  )
}

/** Inset glass box for addresses and contact details. */
export function InfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div
      data-text-block
      className="rounded-2xl border border-white/15 bg-white/[0.05] p-4"
      style={{ boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.18)' }}
    >
      {children}
    </div>
  )
}
